import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

/**
 * RequireAdminWithStepUp — guards /bloom-gestion-secure routes after the
 * Phase 2 cutover. Replaces the legacy AdminAuthGate which was tied to
 * Path 1's admin JWT in localStorage.
 *
 * Decision tree:
 *   - useAuth still loading → spinner
 *   - no user (not logged in) → redirect / with login toast
 *   - user but role != admin → redirect / with "non autorisé" toast
 *   - admin but admin_step_up_verified false → redirect to OAuth flow
 *     (login() triggers the redirect; on return AuthCallbackPage will
 *      see requires_2fa and route to /verify-totp)
 *   - admin AND step-up verified → render children
 */
export default function RequireAdminWithStepUp({ children }) {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      toast.error('Connecte-toi pour accéder au back-office.');
      login();
      return;
    }
    if (user.role !== 'admin') {
      toast.error('Accès non autorisé.');
      return;
    }
    if (!user.admin_step_up_verified) {
      toast.info('Vérification 2FA requise. Reconnecte-toi pour valider ton TOTP.');
      login();
    }
  }, [user, loading, login]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1514] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
      </div>
    );
  }
  if (!user) return null; // useEffect above triggers OAuth login redirect
  if (user.role !== 'admin') return <Navigate to="/" replace state={{ from: location }} />;
  if (!user.admin_step_up_verified) return null; // useEffect triggers re-auth

  return children;
}
